import React, { Component } from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Checklist } from '../../components/Checklist';
import { PERSONAL_IR_CHECKLIST } from '../../utils/constants';
import { AlertTriangle, Shield, Lock, Smartphone, ArrowRight, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
export function IncidentResponse() {
  return <Layout data-id="element-4681">
      <div className="container mx-auto px-4 py-12" data-id="element-4682">
        {/* Hero */}
        <div className="mb-12" data-id="element-4683">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-500 text-xs font-medium mb-4 border border-blue-500/20" data-id="element-4684">
            <AlertTriangle className="h-3 w-3 mr-2" data-id="element-4685" />
            Personal Protocol
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-4686">
            Personal Incident Response
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-4687">
            Know exactly what to do when things go wrong. Panic is the enemy; a
            plan is the solution.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8" data-id="element-4688">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-4689">
            {/* Why This Matters */}
            <section data-id="element-4690">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4691">
                Why This Matters
              </h2>
              <div className="bg-slate-900/50 border-l-4 border-blue-500 p-6 rounded-r-lg" data-id="element-4692">
                <p className="text-slate-300 mb-4" data-id="element-4693">
                  Incidents are inevitable. Whether it's a lost phone, a hacked
                  account, or a data breach, how you respond in the first few
                  hours determines the impact.
                </p>
                <ul className="space-y-2 text-slate-400" data-id="element-4694">
                  <li className="flex items-start" data-id="element-4695">
                    <span className="text-blue-500 mr-2" data-id="element-4696">•</span> Speed is
                    critical—attackers move fast
                  </li>
                  <li className="flex items-start" data-id="element-4697">
                    <span className="text-blue-500 mr-2" data-id="element-4698">•</span> Preparation
                    reduces panic and mistakes
                  </li>
                  <li className="flex items-start" data-id="element-4699">
                    <span className="text-blue-500 mr-2" data-id="element-4700">•</span> Many incidents
                    can be contained if caught early
                  </li>
                </ul>
              </div>
            </section>

            {/* Incident Types */}
            <section data-id="element-4701">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4702">
                Incident Playbooks
              </h2>
              <div className="grid gap-6" data-id="element-4703">
                <Link to="/ir/account-takeover" className="block" data-id="element-4704">
                  <ComponentCard title="Account Takeover" desc="What to do if your email, social media, or banking account is hacked." icon={Lock} link data-id="element-4705" />
                </Link>
                <Link to="/ir/lost-device" className="block" data-id="element-4706">
                  <ComponentCard title="Lost or Stolen Device" desc="Immediate actions to take if your phone or laptop goes missing." icon={Smartphone} link data-id="element-4707" />
                </Link>
                <ComponentCard title="Data Breach" desc="Steps to take if your personal information is exposed in a breach." icon={Shield} data-id="element-4708" />
                <ComponentCard title="Phishing Attack" desc="How to respond if you clicked a malicious link or entered credentials." icon={Activity} data-id="element-4709" />
              </div>
            </section>

            {/* 24-Hour Plan */}
            <section data-id="element-4710">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4711">
                24-Hour Response Plan
              </h2>
              <div className="space-y-4" data-id="element-4712">
                <Step number={1} text="Contain: Isolate the compromised account or device immediately." data-id="element-4713" />
                <Step number={2} text="Assess: Determine what was accessed and the scope of the damage." data-id="element-4714" />
                <Step number={3} text="Recover: Change passwords, restore from backup, and regain control." data-id="element-4715" />
                <Step number={4} text="Prevent: Update security settings to prevent recurrence." data-id="element-4716" />
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-4717">
            <Card className="p-6 sticky top-24" data-id="element-4718">
              <h3 className="text-lg font-bold text-slate-100 mb-4" data-id="element-4719">
                Preparation Checklist
              </h3>
              <Checklist items={PERSONAL_IR_CHECKLIST} id="personal-ir" data-id="element-4720" />

              <div className="mt-8 pt-6 border-t border-slate-800" data-id="element-4721">
                <h4 className="text-sm font-bold text-slate-400 uppercase mb-4" data-id="element-4722">
                  Quick Actions
                </h4>
                <Link to="/tools/breach-check" className="block w-full" data-id="element-4723">
                  <Button className="w-full bg-blue-500 hover:bg-blue-600 text-slate-950" data-id="element-4724">
                    Check for Breaches <ArrowRight className="ml-2 h-4 w-4" data-id="element-4725" />
                  </Button>
                </Link>
                <Link to="/personal/data-backup" className="block w-full mt-3" data-id="element-4726">
                  <Button variant="secondary" className="w-full" data-id="element-4727">
                    Verify Backups
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}
function ComponentCard({
  title,
  desc,
  icon: Icon,
  link
}: {
  title: string;
  desc: string;
  icon: any;
  link?: boolean;
}) {
  return <div className={`bg-slate-900 p-6 rounded-lg border border-slate-800 flex items-start ${link ? 'hover:border-blue-500 transition-colors' : ''}`} data-id="element-4728">
      <div className="bg-blue-500/10 p-3 rounded-lg mr-4 flex-shrink-0" data-id="element-4729">
        <Icon className="h-6 w-6 text-blue-500" data-id="element-4730" />
      </div>
      <div className="flex-grow" data-id="element-4731">
        <div className="flex justify-between items-center" data-id="element-4732">
          <h3 className="text-lg font-bold text-slate-200 mb-2" data-id="element-4733">{title}</h3>
          {link && <ArrowRight className="h-4 w-4 text-slate-500" data-id="element-4734" />}
        </div>
        <p className="text-slate-400 text-sm" data-id="element-4735">{desc}</p>
      </div>
    </div>;
}
function Step({
  number,
  text
}: {
  number: number;
  text: string;
}) {
  return <div className="flex items-center bg-slate-900/50 p-4 rounded border border-slate-800" data-id="element-4736">
      <div className="w-8 h-8 rounded-full bg-slate-800 text-blue-500 font-bold flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-4737">
        {number}
      </div>
      <p className="text-slate-300" data-id="element-4738">{text}</p>
    </div>;
}